/* eslint-disable import/no-mutable-exports */
import { InputVarType } from '@/models/common';
import { ConnectionConfig } from '@rapidaai/react';
import devConfig from './config.development.json';
import prodConfig from './config.production.json';

export interface SentryAnalyticsConfig {
  dsn: string;
  tracePropagationTargets: string[];
}

export interface WorkspaceConfig {
  domain: string;
  title: string;
  logo?: {
    light: string;
    dark: string;
  };
  authentication: {
    signIn: {
      providers: Record<'password' | 'google' | 'linkedin' | 'github', boolean>;
    };
    signUp: {
      enable: boolean;
      providers: Record<'password' | 'google' | 'linkedin' | 'github', boolean>;
    };
    passwordRules?: {
      minLength?: number;
      requireUppercase?: boolean;
      requireLowercase?: boolean;
      requireNumber?: boolean;
      requireSpecialChar?: boolean;
    };
  };
  features?: {
    knowledge?: boolean;
    telemetry?: boolean;
  };
}

export interface RapidaConfig {
  connection: {
    media: string;
    assistant: string;
    web: string;
    endpoint: string;
    sip?: string;
    socket?: string;
  };
  analytics?: SentryAnalyticsConfig;
  workspace: WorkspaceConfig;
}
export const getConfig = (): RapidaConfig => {
  const env = process.env.NODE_ENV || 'development';
  return env === 'production' ? prodConfig : devConfig;
};

// Resolve an empty connection-URL string against the page origin so the
// same UI bundle works on any host that proxies /v1/* and the gRPC-web
// service paths. Each Rapida droplet's nginx proxies these routes to the
// local web-api / assistant-api / endpoint-api containers, so a relative
// host means "use whichever droplet served this page".
//
// Non-empty values are passed through untouched — useful when a deployment
// genuinely needs a cross-host API (e.g. a CDN-served static UI talking to
// a separate API gateway).
const resolveConnection = (
  conn: RapidaConfig['connection'],
): RapidaConfig['connection'] => {
  if (typeof window === 'undefined') return conn;
  const origin = window.location.origin;
  // socket / sip values are bare host:port, not URLs — derive from the
  // current hostname (without protocol) when empty.
  const bareHost = window.location.hostname;
  return {
    assistant: conn.assistant || origin,
    web: conn.web || origin,
    endpoint: conn.endpoint || origin,
    media: conn.media || origin,
    socket: conn.socket || bareHost,
    sip: conn.sip || bareHost,
  };
};

const baseConfig = getConfig();
export const CONFIG: RapidaConfig = {
  ...baseConfig,
  connection: resolveConnection(baseConfig.connection),
};
export const connectionConfig = new ConnectionConfig(CONFIG.connection);

//
export const MAX_PROMPT_MESSAGE_LENGTH = 10;
export const MAX_VAR_KEY_LENGHT = 100;
export const VAR_ITEM_TEMPLATE = {
  name: '',
  type: 'string',
  defaultvalue: '',
};

export const zhRegex = /^[\u4E00-\u9FA5]$/m;
const MAX_ZN_VAR_NAME_LENGHT = 8;
const MAX_EN_VAR_VALUE_LENGHT = 30;
export const getMaxVarNameLength = (value: string) => {
  if (zhRegex.test(value)) return MAX_ZN_VAR_NAME_LENGHT;
  return MAX_EN_VAR_VALUE_LENGHT;
};

export const CONTEXT_PLACEHOLDER_TEXT = '{{#context#}}';
export const HISTORY_PLACEHOLDER_TEXT = '{{#histories#}}';
export const QUERY_PLACEHOLDER_TEXT = '{{#query#}}';
export const PRE_PROMPT_PLACEHOLDER_TEXT = '{{#pre_prompt#}}';

export const SUPPORTED_PROMPT_VARIABLE_TYPE = () => {
  return [
    InputVarType.textInput,
    InputVarType.paragraph,
    InputVarType.number,
    InputVarType.url,
    InputVarType.json,
  ];
};
